import { useQueryClient } from "@tanstack/react-query";
import { Spin } from "antd";
import { useEffect, useMemo, useRef } from "react";
import { RouterProvider } from "react-router";
import NoCompanyError from "./components/NoCompanyError";
import { createRoleRouter } from "./routers";
import { useGetCurrentUser } from "./services/userService";
import { useUserStore } from "./stores/userStore";

function App() {
  const queryClient = useQueryClient();
  const token = useUserStore((state) => state.token);
  const role = useUserStore((state) => state.getRole());
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const hasCompanies = useUserStore((state) => state.hasCompanies());
  const updateUser = useUserStore((state) => state.updateUser);

  // 2. Track the PREVIOUS token
  const prevToken = useRef(token);

  // Invalidate currentUser query when impersonation state changes
  // This ensures fresh data is fetched with the new token
  useEffect(() => {
    // 3. If token changes (regardless of impersonation level), invalidate
    if (token && prevToken.current !== token) {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      prevToken.current = token;
    }
  }, [token, queryClient]);

  // Only fetch current user if authenticated
  const { data: currentUser, isLoading } = useGetCurrentUser(isAuthenticated);

  useEffect(() => {
    // Update user data when current user is fetched
    if (role !== "Unauthenticated" && currentUser) {
      const superAdminStore = useUserStore.getState().superAdminStore;
      const isCurrentlyImpersonating = !!superAdminStore;

      // When impersonating, the currentUser query might still have stale data
      // from the previous user. Skip processing if the fetched user doesn't match
      // the store's current user (which was already set by beginImpersonation).
      const storeUser = useUserStore.getState().user;
      if (storeUser && currentUser.data.id !== storeUser.id) {
        // Stale data — skip until the query refetches with the correct token
        return;
      }

      updateUser(currentUser.data);

      const userCompanies = currentUser.data.companies;
      const currentCompanyId = useUserStore.getState().currentCompany?.id;

      // Update current company with fresh data from /current API
      if (currentUser.data.role === "Super" || currentUser.data.role === "Finance") {
        // Only null out company if we're NOT impersonating
        // During impersonation, a Super Admin's stale data should never reach here
        // (guarded above), but double-check just in case
        if (!isCurrentlyImpersonating) {
          useUserStore.getState().setCurrentCompany(null);
        }
      } else if (currentCompanyId && userCompanies && userCompanies.length > 0) {
        // Find the matching company from fresh data to get all fields including invoicingAccounting
        const updatedCompany = userCompanies.find((c) => c.id === currentCompanyId);
        if (updatedCompany) {
          useUserStore.getState().setCurrentCompany(updatedCompany);
        }
        // If not found, keep existing currentCompany as-is
      } else if (!currentCompanyId && !isCurrentlyImpersonating && userCompanies && userCompanies.length > 0) {
        // Set default company if not set and not impersonating
        useUserStore.getState().setCurrentCompany(userCompanies[0]);
      }
    }
  }, [role, currentUser, updateUser]);

  // Create router based on user role and company - memoized to prevent unnecessary recreations
  const user = useUserStore((state) => state.user);
  const currentCompany = useUserStore((state) => state.currentCompany);
  const router = useMemo(() => createRoleRouter(role, user), [role, user, currentCompany]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen py-20">
        <Spin size="large" />
      </div>
    );
  }

  // Show error if authenticated user has no companies. PowerBiUser is exempt like PowerBiAdmin:
  // unlike Admin/Employee/User, a company-less PowerBiUser should still be able to log in (they'll
  // just have no dashboards to view until one is assigned) rather than being hard-blocked here —
  // their branding is always Vertoz's own regardless of company (see useTheme.ts), so there's
  // nothing company-dependent blocking them from reaching a normal, if empty, Power BI page.
  if (
    isAuthenticated &&
    role !== "Unauthenticated" &&
    role !== "Super" &&
    role !== "PowerBiAdmin" &&
    role !== "PowerBiUser" &&
    !hasCompanies
  ) {
    return <NoCompanyError />;
  }

  return <RouterProvider router={router} />;
}

export default App;
